import { Ref, useMemo, type FC } from "react";
import { Grid, Group, Stack, UnstyledButton } from "@mantine/core";
import Text from "@/components/atoms/Text";
import { useTranslation } from "react-i18next";
import { getContractTradeType } from "@/utils/Contracts";
import { useAuth } from "@/contexts/AuthContext";
import ContractTradeTypeEnum from "@/constants/enums/ContractTradeTypeEnum";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { LocationAndDeliveryFormInput, getLocationAndDeliverySchema } from "../validation/locationAndDelivery";
import { findDeliveryAddress } from "@/utils/AddressHelper";
import TextInput from "@/components/atoms/TextInput";
import SelectCountry from "@/components/molecules/SelectInputs/SelectCountry";
import Divider from "@/components/atoms/Divider";
import SelectProductBoxType from "@/components/molecules/SelectInputs/SelectProductBoxType";
import DatePicker from "@/components/molecules/DatePicker";
import { Contract } from "@/types";
import { determineIfEstimatedDeliveryIsRequired } from "@/components/organisms/CreateOrderFromContract/helpers";
import { useGetCompanyById } from "@/services/Company";

interface Props {
  onValidationSuccess: (data: any) => void;
  onChangeWizardData: (changed: any) => void;
  contract: Contract;
  ref?: Ref<HTMLFormElement>;
}

const LocationAndDeliveryStep: FC<Props & { ref?: React.Ref<HTMLFormElement> }> = ({
  onValidationSuccess,
  onChangeWizardData,
  contract,
  ref,
}) => {
  const { t } = useTranslation();
  const { currentCompany } = useAuth();

  const { data: company } = useGetCompanyById(currentCompany?.companyId as number, {
    enabled: !!currentCompany?.companyId,
  });

  const contractTradeType = getContractTradeType(contract!, currentCompany);

  const isDeliveryTimeRequired = determineIfEstimatedDeliveryIsRequired(
    company?.isOrderPlanningEnabled ?? true,
    contract.automaticSalesApproval
  );

  const contractAddressDefaults = useMemo(() => {
    const addressSource =
      contractTradeType === ContractTradeTypeEnum.BUY
        ? contract?.contractBuyers[0].companyAddress[0]
        : contract?.contractSellers[0].companyAddress[0];
    return {
      address: addressSource?.address ?? "",
      postCode: addressSource?.postCode ?? "",
      countryId: addressSource?.country.id,
      place: addressSource?.place ?? "",
    };
  }, [contract, contractTradeType]);

  const { handleSubmit, control, watch, setValue } = useForm<LocationAndDeliveryFormInput>({
    resolver: zodResolver(getLocationAndDeliverySchema(isDeliveryTimeRequired)),
    defaultValues: {
      packaging: 1,
      differentAddress: false,
      address: contractAddressDefaults.address,
      postCode: contractAddressDefaults.postCode,
      countryId: contractAddressDefaults.countryId,
      place: contractAddressDefaults.place,
      estimatedDelivery: undefined,
    },
  });

  /*   const determineIfTransportResponsible = (contract: Contract, companyId: number): boolean => {
    if (contract.transportResponsible === "REVIVER") {
      return false;
    } else if (contract.transportResponsible === "BUYER" && contract.contractBuyers.some((b) => b.id === companyId)) {
      return true;
    } else if (contract.transportResponsible === "SELLER" && contract.contractSellers.some((s) => s.id === companyId)) {
      return true;
    }
    return false;
  }; */

  const onSubmit = async (data: LocationAndDeliveryFormInput) => {
    onChangeWizardData(data);
    onValidationSuccess(data);
  };

  return (
    <form ref={ref} onSubmit={handleSubmit(onSubmit)}>
      <Grid>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Stack justify="flex-start">
            {/* YOUR COMPANY */}
            <Group p={0} gap={0} bg="transparent" align="center">
              <Text color="secondary" size="sm" data-testid={"company-deliver-label"}>
                {contractTradeType === ContractTradeTypeEnum.BUY
                  ? t("contracts:deliverTo")
                  : t("contracts:deliverFrom")}
              </Text>
              <UnstyledButton
                onClick={() => setValue("differentAddress", !watch("differentAddress"))}
                data-testid={"change-address-btn"}
              >
                <Text
                  ml={10}
                  color="secondary"
                  size="xs"
                  td="underline"
                  style={{
                    cursor: "pointer",
                    color: "var(--color-text-secondary)",
                  }}
                  data-testid={"change-address-btn-text"}
                >
                  {t("actions:changeAddress")}
                </Text>
              </UnstyledButton>
            </Group>
            {contractTradeType === ContractTradeTypeEnum.BUY && contract?.contractBuyers[0].companyAddress && (
              <Text size="lg" strong={true} data-testid={"company-deliver-address"}>
                {findDeliveryAddress(contract.contractBuyers[0].companyAddress)}
              </Text>
            )}
            {contractTradeType === ContractTradeTypeEnum.SELL && contract?.contractSellers[0].companyAddress && (
              <Text size="lg" strong={true} data-testid={"company-deliver-address"}>
                {findDeliveryAddress(contract.contractSellers[0].companyAddress)}
              </Text>
            )}
          </Stack>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          {/* THEIR COMPANY */}
          <Stack>
            <Group p={0} gap={0} bg="transparent" align="center">
              <Text color="secondary" size="sm" data-testid={"counterpart-deliver-label"}>
                {contractTradeType === ContractTradeTypeEnum.BUY
                  ? t("contracts:deliverFrom")
                  : t("contracts:deliverTo")}
              </Text>
            </Group>
            {contractTradeType === ContractTradeTypeEnum.BUY && contract?.contractSellers[0].companyAddress && (
              <Text size="lg" strong={true} data-testid={"counterpart-deliver-address"}>
                {findDeliveryAddress(contract.contractSellers[0].companyAddress)}
              </Text>
            )}
            {contractTradeType === ContractTradeTypeEnum.SELL && contract?.contractBuyers[0].companyAddress && (
              <Text size="lg" strong={true} data-testid={"counterpart-deliver-address"}>
                {findDeliveryAddress(contract.contractBuyers[0].companyAddress)}
              </Text>
            )}
          </Stack>
        </Grid.Col>

        <Grid.Col span={12}>
          <Divider />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Controller
            control={control}
            name="address"
            render={({ field, fieldState }) => (
              <TextInput
                data-testid={"address-input"}
                label={t("orders:orderAddress")}
                disabled={!watch("differentAddress")}
                error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                {...field}
              />
            )}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Controller
            control={control}
            name="countryId"
            render={({ field: { onChange, value }, fieldState }) => (
              <SelectCountry
                labelKey="name"
                valueKey="id"
                data-testid={"country-select"}
                label={t("common:country")}
                disabled={!watch("differentAddress")}
                value={value?.toString()}
                onChange={(val) => onChange(val ? Number(val) : null)}
                error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
              />
            )}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Controller
            control={control}
            name="postCode"
            render={({ field, fieldState }) => (
              <TextInput
                id="postCode-order-from-contract"
                data-testid={"postCode-input"}
                label={t("orders:postCode")}
                disabled={!watch("differentAddress")}
                error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                {...field}
              />
            )}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Controller
            control={control}
            name="place"
            render={({ field, fieldState }) => (
              <TextInput
                id="postPlace"
                data-testid={"place-input"}
                label={t("orders:place")}
                disabled={!watch("differentAddress")}
                error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                {...field}
              />
            )}
          />
        </Grid.Col>

        <Grid.Col span={12}>
          <Divider />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Controller
            control={control}
            name="packaging"
            render={({ field: { onChange, value } }) => (
              <SelectProductBoxType
                id={"boxType-select"}
                labelKey="description"
                valueKey="id"
                data-testid={"boxType-select"}
                label={t("product:packaging")}
                value={value.toString()}
                onChange={onChange}
              />
            )}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Controller
            control={control}
            name="estimatedDelivery"
            render={({ field: { onChange, value }, fieldState }) => {
              return (
                <DatePicker
                  testId="estimated-delivery"
                  label={t("contracts:estimatedDelivery")}
                  required={isDeliveryTimeRequired}
                  disabled={!contract.automaticSalesApproval}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                  value={value ?? null}
                  onChange={onChange}
                  minDate={new Date()}
                  showTimePicker
                />
              );
            }}
          />
        </Grid.Col>
      </Grid>
    </form>
  );
};

export default LocationAndDeliveryStep;
